import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import ModalContext from '../../../../contexts/ModalContext';
import StockContext from '../../../../contexts/StockContext';
import { ProductionFashionOutletType } from '..';
import { TypeMainOutlet } from '../..';
import { GlobalTable, HeaderLine, Td, Th, Tr, FooterTr } from '../../../../components/styles/table-style';
import LoadMoreButton from '../../../../components/LoadMoreButton';
import ApiService from '../../../../services/ApiService';
import FashionApiService from '../../../../services/FashionApiService';
import { format } from 'date-fns';
import TableFiltersContext from '../../../../contexts/TableFiltersContext';
import { TableContainer } from '../style';
import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react';

const CELL_EMPTY = '-';

function fashionMovementStatusCode(status: unknown): string {
  return String(status ?? '').trim();
}

function isFashionMovementReverted(item: { status?: unknown }): boolean {
  const s = fashionMovementStatusCode(item.status);
  return s === '410' || s.startsWith('410');
}

function fashionMovementStatusLabel(item: { status?: unknown }): string {
  if (isFashionMovementReverted(item)) return 'Desfeito';
  return 'Abatido';
}

function parseQuantitySuffix(q: unknown): { num: number; suffix: string } | null {
  const s = String(q ?? "").trim();
  const m = s.match(/^([\d.,]+)\s*([a-zA-Zºªá-úÁ-Ú%./\-*]+.*)$/);
  if (!m) return null;
  const num = parseFloat(m[1].replace(",", "."));
  if (!Number.isFinite(num)) return null;
  return { num, suffix: m[2].trim() };
}

function isMeterLengthSuffix(suffix: string): boolean {
  return suffix.toUpperCase() === "M";
}

function isMillimeterSuffix(suffix: string): boolean {
  return suffix.toUpperCase() === "MM";
}

function isPieceUnitSuffix(suffix: string): boolean {
  const u = suffix.toUpperCase();
  return u === "UN" || u === "UNIDADE" || u === "UN.";
}

function displayFashionMovementQuantity(
  item: FashionProductionMovementRow,
  formatNum: (v: number | null | undefined) => string,
): string {
  if (item.isBuyOrder) return String(item.quantity ?? CELL_EMPTY);
  const parsed = parseQuantitySuffix(item.quantity);
  if (!parsed) return String(item.quantity ?? CELL_EMPTY);
  const { num, suffix } = parsed;
  if (isMillimeterSuffix(suffix)) return `${formatNum(num)} MM`;
  if (!isMeterLengthSuffix(suffix)) {
    const qty = isPieceUnitSuffix(suffix) ? String(Math.round(num)) : formatNum(num);
    return `${qty} ${suffix.toUpperCase()}`;
  }
  const deltaLen = (item.oldLengthInMm ?? 0) - (item.newLengthInMm ?? 0);
  if (deltaLen > 1e-6) {
    if (Math.abs(num - deltaLen) < 1) return `${formatNum(num / 1000)} M`;
    if (Math.abs(num * 1000 - deltaLen) < 1) return `${formatNum(num)} M`;
  }
  return `${formatNum(num)} M`;
}

function displayFashionLengths(
  item: FashionProductionMovementRow,
  formatNum: (v: number | null | undefined) => string,
): { old: string; new: string } {
  const o = item.oldLengthInMm;
  const n = item.newLengthInMm;
  const parsed = parseQuantitySuffix(item.quantity);
  const sfx = parsed?.suffix ?? "";
  const hasNumericLength =
    (o != null && Math.abs(o) > 1e-9) ||
    (n != null && Math.abs(n) > 1e-9) ||
    (o != null && n != null && Math.abs(o - n) > 1e-6);
  if (!hasNumericLength) return { old: CELL_EMPTY, new: CELL_EMPTY };
  if (isMeterLengthSuffix(sfx) && !isMillimeterSuffix(sfx) && o != null && n != null) {
    return {
      old: `${formatNum(o / 1000)} M`,
      new: `${formatNum(n / 1000)} M`,
    };
  }
  return {
    old: `${formatNum(o)} MM`,
    new: `${formatNum(n)} MM`,
  };
}

function isUnitAbatementRow(item: FashionProductionMovementRow): boolean {
  if (item.isBuyOrder) return false;
  return item.oldQuantity != null && item.newQuantity != null && (item.oldQuantity > 0 || item.newQuantity > 0);
}

function isLengthAbatementRow(item: FashionProductionMovementRow): boolean {
  if (item.isBuyOrder) return false;
  if (isUnitAbatementRow(item)) return false;
  const o = item.oldLengthInMm ?? 0;
  const n = item.newLengthInMm ?? 0;
  return Math.abs(o - n) > 1e-6 || o > 1e-6 || n > 1e-6;
}

function shouldShowFashionWeightColumns(item: FashionProductionMovementRow): boolean {
  if (item.isBuyOrder) return false;
  const ow = item.oldTotalWeight ?? 0;
  const nw = item.newTotalWeight ?? 0;
  return Math.abs(ow) > 1e-6 || Math.abs(nw) > 1e-6;
}

function displayFashionWeights(
  item: FashionProductionMovementRow,
  formatNum: (v: number | null | undefined) => string,
): { old: string; new: string } {
  return {
    old: `${formatNum(item.oldTotalWeight)} KG`,
    new: `${formatNum(item.newTotalWeight)} KG`,
  };
}

function fashionOpIdFromOrderName(orderName: string | null | undefined): number | null {
  const str = String(orderName ?? '');
  const m = str.match(/^OP-FASHION-(\d+)\|/) || str.match(/^OPF-(\d+)/);
  return m ? Number(m[1]) : null;
}

function insumoResourceIdFromRaw(raw: string): number | null {
  const m = /^__INS_RES_(\d+)__$/.exec(raw);
  return m ? Number(m[1]) : null;
}

function materialCodeFromRaw(raw: string): string {
  return raw.replace(/__\d+$/, '').trim();
}

function extractAxiosData(response: { data?: unknown } | null | undefined): unknown {
  return response?.data ?? response;
}

async function loadPedidoLabelsForOpIds(opIds: number[]): Promise<Record<number, string>> {
  const out: Record<number, string> = {};
  await Promise.all(
    opIds.map(async (id) => {
      try {
        const res = await FashionApiService.getProductionOrderById(id);
        const data = extractAxiosData(res) as {
          FashionProductionItems?: { fashionOrderId: number }[];
        };
        const items = data?.FashionProductionItems ?? [];
        out[id] = items.length
          ? items.map((it) => `PED-${it.fashionOrderId}`).join(', ')
          : 'N/A';
      } catch {
        out[id] = 'N/A';
      }
    }),
  );
  return out;
}

async function loadInsumoNames(ids: number[]): Promise<Record<number, string>> {
  const out: Record<number, string> = {};
  await Promise.all(
    ids.map(async (id) => {
      try {
        const res = await ApiService.resources.findById(id);
        const data = extractAxiosData(res) as { material?: string; name?: string };
        out[id] = String(data?.material || data?.name || `#${id}`);
      } catch {
        out[id] = `Insumo #${id}`;
      }
    }),
  );
  return out;
}

async function loadStockDescriptionByCode(codes: string[]): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  await Promise.all(
    codes.map(async (code) => {
      try {
        const res = await ApiService.stock.findAll({ global: code, size: 40 });
        const payload = extractAxiosData(res) as unknown;
        const list = Array.isArray(payload)
          ? payload
          : (payload as { items?: unknown[]; data?: unknown[] })?.items ??
            (payload as { data?: unknown[] })?.data ??
            [];
        const row = Array.isArray(list)
          ? list.find(
              (s: { Code?: { code?: string }; code?: string }) =>
                String(s?.Code?.code ?? s?.code ?? "") === code,
            )
          : null;
        const stockRow = row as { material?: string; description?: string } | null;
        const matName = stockRow?.material || stockRow?.description;
        const trimmed = matName != null ? String(matName).trim() : "";
        out[code] = trimmed !== "" ? trimmed : code;
      } catch {
        out[code] = code;
      }
    }),
  );
  return out;
}

function displayFashionMaterialLabel(
  raw: string,
  insumoNames: Record<number, string>,
  stockDescriptionByCode: Record<string, string>,
): string {
  const ins = insumoResourceIdFromRaw(raw);
  if (ins != null) return insumoNames[ins] ?? `Insumo #${ins}`;
  const code = materialCodeFromRaw(raw);
  return stockDescriptionByCode[code] ?? code;
}

function formatFashionUnitQuantity(value: number | null | undefined): string {
  if (value == null || typeof value !== 'number') return CELL_EMPTY;
  return String(Math.round(value));
}

interface User {
  name: string;
}
interface FashionProductionMovementRow {
  id: number;
  ploomesOrderId: number;
  rawMaterialName: string;
  status: string;
  quantity: string | number;
  oldTotalWeight: number;
  newTotalWeight: number;
  oldLengthInMm: number;
  newLengthInMm: number;
  oldQuantity?: number;
  newQuantity?: number;
  createdAt: string;
  orderName: string;
  updatedBy: User;
  updatedAt: string;
  stockId?: number | null;
  fashionConsumedStockId?: number | null;
  fashionConsumedResourceId?: number | null;
  isBuyOrder?: boolean;
  buyOrderStatus?: string;
  [key: string]: unknown; 
}

const FashionProductionMovementTable: React.FC = () => {
  const navigate = useNavigate();
  const { setModalActive, setModalSection, setStockMode } = useContext(ModalContext);
  const { setStock } = useContext(StockContext);
  const { setTabIndex, quantity, setQuantity, refresh } = useOutletContext<ProductionFashionOutletType>();
  const { search } = useOutletContext<TypeMainOutlet>();
  const { movementFilters, movementTypeFilter } = useContext(TableFiltersContext);
  const [movements, setMovements] = useState<FashionProductionMovementRow[]>([]);
  const [totalMovements, setTotalMovements] = useState<number | null>(null);
  const [lastReturnedCount, setLastReturnedCount] = useState<number>(0);
  const [pedidoByOpId, setPedidoByOpId] = useState<Record<number, string>>({});
  const [insumoNameById, setInsumoNameById] = useState<Record<number, string>>({});
  const [stockDescriptionByCode, setStockDescriptionByCode] = useState<Record<string, string>>({});

  const normalizeMovements = (raw: any): FashionProductionMovementRow[] => {
    if (Array.isArray(raw)) {
      return raw as FashionProductionMovementRow[];
    }
    if (raw?.data && Array.isArray(raw.data)) {
      return raw.data as FashionProductionMovementRow[];
    }
    if (raw?.items && Array.isArray(raw.items)) {
      return raw.items as FashionProductionMovementRow[];
    }
    if (raw?.results && Array.isArray(raw.results)) {
      return raw.results as FashionProductionMovementRow[];
    }

    if (raw && typeof raw === 'object') {
      const firstArray = Object.values(raw).find((value) => Array.isArray(value)) as FashionProductionMovementRow[] | undefined;
      if (Array.isArray(firstArray)) {
        return firstArray;
      }
    }

    return [];
  };

  useEffect(() => {
    setTabIndex(1);
  }, [setTabIndex]);

  useEffect(() => {
    const fetchMovements = async () => {
      try {
        const parameters = {
          size: quantity,
          filters: movementFilters,
          global: search,
          movementType: movementTypeFilter,
          fashionOnly: true,
        };

        const responseMovements = await ApiService.movements.find(parameters);
        const rawData = responseMovements?.data;
        const extractedMovements = normalizeMovements(rawData);

        let extractedBuyOrders: any[] = [];
        try {
          const responseBuyOrders = await ApiService.buyOrder.findAll();
          extractedBuyOrders = responseBuyOrders?.data || [];
        } catch (err) {
          console.error('Erro ao buscar ordens de compra:', err);
        }

        const unifiedMovements = extractedMovements.map(m => {
          let direction: 'IN' | 'OUT' = 'OUT';
          const oldW = m.oldTotalWeight ?? 0;
          const newW = m.newTotalWeight ?? 0;
          const oldL = m.oldLengthInMm ?? 0;
          const newL = m.newLengthInMm ?? 0;
          const oldQ = m.oldQuantity ?? 0;
          const newQ = m.newQuantity ?? 0;

          if (Math.abs(newW - oldW) > 1e-6) {
            direction = newW > oldW ? 'IN' : 'OUT';
          } else if (Math.abs(newL - oldL) > 1e-6) {
            direction = newL > oldL ? 'IN' : 'OUT';
          } else if (Math.abs(newQ - oldQ) > 1e-6) {
            direction = newQ > oldQ ? 'IN' : 'OUT';
          } else {
            const statusCode = fashionMovementStatusCode(m.status);
            direction = statusCode === '201' ? 'IN' : 'OUT';
          }

          return {
            ...m,
            isBuyOrder: false,
            direction,
          };
        });

        const unifiedBuyOrders = extractedBuyOrders.map(bo => {
          const rawName = bo.StockInProduction?.Stock?.Code?.code || bo.StockInProduction?.Resource?.name || 'Item OC';
          const formattedQty = `${bo.quantityToBuy ?? bo.quantityRequired} ${bo.unitOfMeasurement || ''}`.trim();
          
          const statusTranslation: Record<string, string> = {
            PENDING: 'Pendente',
            APPROVED: 'Aprovada',
            ORDERED: 'Encomendada',
            DELIVERED: 'Comprada',
            COMPLETED: 'Concluída',
            CANCELLED: 'Cancelada',
          };

          return {
            id: bo.id,
            ploomesOrderId: 0,
            rawMaterialName: bo.StockInProduction?.Resource ? `__INS_RES_${bo.StockInProduction.resourceId}__` : rawName,
            status: bo.status,
            quantity: formattedQty,
            oldTotalWeight: 0,
            newTotalWeight: 0,
            oldLengthInMm: 0,
            newLengthInMm: 0,
            createdAt: bo.createdAt,
            orderName: bo.StockInProduction?.Services?.id ? `OP-FASHION-${bo.StockInProduction.Services.id}|` : 'Ordem de Compra',
            updatedBy: bo.CreatedBy ? { name: bo.CreatedBy.name } : { name: 'Sistema' },
            updatedAt: bo.createdAt,
            stockId: bo.StockInProduction?.stockId,
            fashionConsumedStockId: bo.StockInProduction?.stockId,
            fashionConsumedResourceId: bo.StockInProduction?.resourceId,
            isBuyOrder: true,
            buyOrderStatus: statusTranslation[bo.status] || bo.status,
            direction: 'OC',
          };
        });

        const allCombined = [...unifiedMovements, ...unifiedBuyOrders].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setLastReturnedCount(extractedMovements.length);
        setMovements(allCombined as any);
      } catch (error) {
        console.error('Erro ao buscar movimentações e OCs:', error);
      }
    };

    fetchMovements();
  }, [quantity, movementFilters, search, movementTypeFilter, refresh]);

  useEffect(() => {
    const fetchTotalMovements = async () => {
      try {
        const response = await ApiService.movements.getTotal({ 
          filters: movementFilters,
          global: search,
          movementType: movementTypeFilter,
          fashionOnly: true,
        });
        
        if (response?.data != null) {
          const total = typeof response.data === 'number' ? response.data : response.data.total || response.data.count || null;
          setTotalMovements(total);
        }
      } catch (error) {
        console.error('Erro ao buscar total de movimentações:', error);
        setTotalMovements(null);
      }
    };

    fetchTotalMovements();
  }, [movementFilters, movementTypeFilter, search, refresh]);

  useEffect(() => {
    let cancelled = false;
    const opIds = [
      ...new Set(
        movements
          .map((m) => fashionOpIdFromOrderName(String(m.orderName ?? '')))
          .filter((x): x is number => x != null),
      ),
    ];
    const insumoIds = [
      ...new Set(
        movements
          .map((m) => insumoResourceIdFromRaw(String(m.rawMaterialName ?? '')))
          .filter((x): x is number => x != null),
      ),
    ];
    const materialCodes = [
      ...new Set(
        movements
          .map((m) => {
            const raw = String(m.rawMaterialName ?? '');
            if (insumoResourceIdFromRaw(raw) != null) return null;
            return materialCodeFromRaw(raw);
          })
          .filter((x): x is string => Boolean(x)),
      ),
    ];

    (async () => {
      const [pedidos, insumos, stockDesc] = await Promise.all([
        loadPedidoLabelsForOpIds(opIds),
        loadInsumoNames(insumoIds),
        loadStockDescriptionByCode(materialCodes),
      ]);
      if (cancelled) return;
      setPedidoByOpId((prev) => ({ ...prev, ...pedidos }));
      setInsumoNameById((prev) => ({ ...prev, ...insumos }));
      setStockDescriptionByCode((prev) => ({ ...prev, ...stockDesc }));
    })();

    return () => {
      cancelled = true;
    };
  }, [movements]);

  const formatNumber = (value: number | undefined | null) => {
    if (value == null || typeof value !== 'number') return '-';
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const handleMaterialClick = async (item: FashionProductionMovementRow) => {
    const raw = String(item.rawMaterialName ?? '');
    const insId = insumoResourceIdFromRaw(raw);
    const resourceId = item.fashionConsumedResourceId || insId;

    if (resourceId != null) {
      setStock({ id: resourceId, isResource: true } as any);
      setModalSection('viewResource');
      setModalActive(true);
      return;
    }

    const stockId = item.fashionConsumedStockId || item.stockId;
    const code = materialCodeFromRaw(raw);

    try {
      const res = await ApiService.stock.findAll({ global: code, size: 40 });
      const payload = extractAxiosData(res) as any;
      const list = Array.isArray(payload)
        ? payload
        : payload?.items ?? payload?.data ?? [];

      if (Array.isArray(list)) {
        const foundItem =
          (stockId ? list.find((s: any) => s.id === stockId) : null) ||
          list.find((s: any) => String(s?.Code?.code ?? s?.code ?? '') === code);

        if (foundItem) {
          setStock({
            ...foundItem,
            itemColorId: foundItem.itemColorId ?? null,
            color: foundItem.color ?? '',
            pantoneCode: foundItem.pantoneCode ?? '',
            pantoneHex: foundItem.pantoneHex ?? '',
          });
          setStockMode('fashion');
          setModalSection('newModal');
          setModalActive(true);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar estoque para visualização:', error);
    }
  };

  const filteredData = (_item: FashionProductionMovementRow) => {
    return true;
  };

  const hasMore = useMemo(() => {
    if (totalMovements !== null) {
      return movements.length < totalMovements && lastReturnedCount === quantity;
    }
    return lastReturnedCount === quantity && lastReturnedCount > 0;
  }, [movements.length, quantity, totalMovements, lastReturnedCount]);

  return (
    <TableContainer>
      <GlobalTable>
        <thead>
          <HeaderLine>
            <Th style={{ width: '30px', paddingLeft: '8px', paddingRight: '8px' }}></Th>
            <Th>Origem</Th>
            <Th>Matéria prima</Th>
            <Th>Quantidade</Th>
            <Th>Peso anterior (kg)</Th>
            <Th>Peso novo (kg)</Th>
            <Th>Comp. anterior</Th>
            <Th>Comp. novo</Th>
            <Th>Qtd. anterior</Th>
            <Th>Qtd. nova</Th>
            <Th>Status</Th>
            <Th>Data</Th>
          </HeaderLine>
        </thead>
        <tbody>
          {movements.filter(filteredData).map((item) => {
            const lenDisp = displayFashionLengths(item, formatNumber);
            const weightDisp = displayFashionWeights(item, formatNumber);
            const opId = fashionOpIdFromOrderName(String(item.orderName ?? ''));
            const pedidoLabel =
              opId != null ? pedidoByOpId[opId] ?? CELL_EMPTY : CELL_EMPTY;
            
            const originLabel = pedidoLabel !== CELL_EMPTY ? `OP - ${pedidoLabel}` : 'Manual';

            const directionLabel = item.isBuyOrder
              ? 'Ordem de Compra'
              : item.direction === 'IN'
              ? 'Entrada'
              : 'Saída';

            let icon = null;
            if (item.isBuyOrder) {
              icon = <ArrowUpCircle color="#0090d3" size={20} />;
            } else if (item.direction === 'IN') {
              icon = <ArrowUpCircle color="green" size={20} />;
            } else if (item.direction === 'OUT') {
              icon = <ArrowDownCircle color="red" size={20} />;
            }

            const statusText = item.isBuyOrder
              ? item.buyOrderStatus
              : fashionMovementStatusLabel(item);

            return (
            <Tr key={item.id}
              className={isFashionMovementReverted(item) ? 'yellowLine' : ''}
              title={
                item.updatedBy?.name && item.updatedAt
                  ? `Atualizado por ${item.updatedBy.name} em ${
                    format(new Date(item.updatedAt), 'dd/MM/yyyy - HH:mm')}h`
                  : undefined
              }
            >
              <Td style={{ textAlign: 'center', paddingLeft: '8px', paddingRight: '8px', width: '30px' }} title={directionLabel}>{icon}</Td>
              <Td title={item.orderName ? String(item.orderName) : undefined}>{originLabel}</Td>
              <Td>
                <span
                  onClick={() => handleMaterialClick(item)}
                  style={{
                    color: '#0090d3',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                >
                  {displayFashionMaterialLabel(
                    String(item.rawMaterialName ?? ''),
                    insumoNameById,
                    stockDescriptionByCode,
                  )}
                </span>
              </Td>
              <Td>{displayFashionMovementQuantity(item, formatNumber)}</Td>
              <Td>
                {shouldShowFashionWeightColumns(item) ? weightDisp.old : CELL_EMPTY}
              </Td>
              <Td>
                {shouldShowFashionWeightColumns(item) ? weightDisp.new : CELL_EMPTY}
              </Td>
              <Td>{isLengthAbatementRow(item) ? lenDisp.old : CELL_EMPTY}</Td>
              <Td>{isLengthAbatementRow(item) ? lenDisp.new : CELL_EMPTY}</Td>
              <Td>{isUnitAbatementRow(item) ? formatFashionUnitQuantity(item.oldQuantity) : CELL_EMPTY}</Td>
              <Td>{isUnitAbatementRow(item) ? formatFashionUnitQuantity(item.newQuantity) : CELL_EMPTY}</Td>
              <Td>{statusText}</Td>
              <Td>{format(new Date(item.createdAt), 'dd/MM/yyyy - HH:mm')}h</Td>
            </Tr>
            );
          })}
          {movements.length === 0 && (
            <Tr>
              <Td colSpan={12} style={{ textAlign: 'center', padding: '20px' }}>
                Nenhuma movimentação encontrada
              </Td>
            </Tr>
          )}
        </tbody>
        <tfoot>
          <FooterTr>
            <Td
              colSpan={12}
              style={{ textAlign: 'center', background: 'transparent', padding: '1rem 0', width: '100%' }}
            >
              <LoadMoreButton
                currentCount={movements.length}
                totalCount={totalMovements ?? undefined}
                onLoadMore={() => setQuantity((q) => q + 50)}
                hasMore={hasMore}
              />
            </Td>
          </FooterTr>
        </tfoot>
      </GlobalTable>
    </TableContainer>
  );
};

export default FashionProductionMovementTable;